/**
 * 看圖開口說資料 — 小三程度生活場景(長期路線圖 P3b,boss 2026-06-12 拍板)。
 *
 * 設計取捨:**不依賴視覺模型評分**。每個場景帶 `expectedKeywords`(主角 / 動作 /
 * 物品 / 地點),學生看圖自由開口說英文,前端用 Web Speech 辨識逐字稿後算
 * 「場景關鍵字覆蓋率」當分數 —— 純前端、免後端、錄音不上雲。
 *
 * 場景主題對齊翰林三下英語單元(問候 / 教室 / 家人 / 動物 / 食物 / 顏色數字),
 * `sampleSentence` 取材自三下句型程度(be 動詞 / 現在式 / There are / I like)。
 *
 * 圖片 = tools/gen-picture-talk-images.mjs 用 worker /gen-image(Flux Schnell)
 * **離線預產**到 public/img/oral/<id>.png,前端只吃靜態圖,不在執行期生圖。
 *
 * 學習/遊戲分離(CLAUDE.md 紅線):純學習側資料,不碰任何戰鬥數值 / commanderXp。
 */

/** 看圖開口說的一個場景。 */
export interface PictureTalkScene {
  /** 穩定 id(圖檔檔名 / localStorage 追蹤依賴),一律 `pt-` 前綴。 */
  id: string;
  /** 給 /gen-image 的英文 prompt(flat cartoon for children 風格)。 */
  imagePrompt: string;
  /** 場景關鍵字(小寫英文):主角 / 動作 / 物品 / 地點,評分 = 覆蓋率。 */
  expectedKeywords: string[];
  /** 一句示範句(翰林三下句型程度),「聽示範句」用 TTS 念。 */
  sampleSentence: string;
  /** 中文提示(引導小孩看圖說什麼)。 */
  zh: string;
}

const STYLE =
  'flat cartoon illustration for children, bright colors, simple shapes, clean composition, no text, no watermark';

/** 翰林三下看圖開口說 — 14 個生活場景(問候 / 教室 / 家人 / 動物 / 食物 / 顏色數字)。 */
export const PICTURE_TALK_SCENES: PictureTalkScene[] = [
  // ── 問候 ──
  {
    id: 'pt-greet',
    imagePrompt: `${STYLE}, a boy and a girl waving hello to each other at the school gate, both wearing school bags, smiling, morning sun`,
    expectedKeywords: ['boy', 'girl', 'hello', 'wave', 'school', 'bag', 'smile'],
    sampleSentence: 'The boy and the girl say hello at school.',
    zh: '早上在校門口,兩個小朋友在做什麼?',
  },
  {
    id: 'pt-morning',
    imagePrompt: `${STYLE}, a young boy brushing his teeth in front of a bathroom mirror, holding a toothbrush, morning light through window`,
    expectedKeywords: ['boy', 'brush', 'teeth', 'bathroom', 'mirror', 'toothbrush', 'morning'],
    sampleSentence: 'The boy brushes his teeth in the morning.',
    zh: '早上起床後,男孩在浴室做什麼?',
  },
  // ── 教室 ──
  {
    id: 'pt-classroom',
    imagePrompt: `${STYLE}, a teacher standing by a blackboard and students sitting at desks reading open books in a classroom`,
    expectedKeywords: ['teacher', 'student', 'book', 'open', 'read', 'classroom', 'desk', 'blackboard'],
    sampleSentence: 'The students open their books in the classroom.',
    zh: '教室裡有誰?大家在做什麼?',
  },
  {
    id: 'pt-standup',
    imagePrompt: `${STYLE}, students standing up next to their chairs in a classroom, a teacher in front smiling`,
    expectedKeywords: ['student', 'stand', 'up', 'teacher', 'classroom', 'chair'],
    sampleSentence: 'The students stand up in the classroom.',
    zh: '上課鐘響了,同學們正在做什麼動作?',
  },
  // ── 家人 ──
  {
    id: 'pt-family',
    imagePrompt: `${STYLE}, a happy family of four eating dinner together at a round table at home, father mother and two kids, dishes of food`,
    expectedKeywords: ['family', 'father', 'mother', 'dinner', 'table', 'eat', 'food', 'happy'],
    sampleSentence: 'My family eats dinner at the table.',
    zh: '晚餐時間,一家人在做什麼?圖裡有誰?',
  },
  {
    id: 'pt-grandma',
    imagePrompt: `${STYLE}, a grandmother reading a picture book with a little girl on a sofa at home, warm living room`,
    expectedKeywords: ['grandma', 'girl', 'read', 'book', 'sofa', 'home', 'happy'],
    sampleSentence: 'Grandma reads a book with the girl.',
    zh: '客廳沙發上,奶奶和小女孩在做什麼?',
  },
  // ── 動物 ──
  {
    id: 'pt-dog',
    imagePrompt: `${STYLE}, a boy playing with a dog in a park, throwing a ball, green grass and trees`,
    expectedKeywords: ['boy', 'dog', 'play', 'ball', 'park', 'run', 'grass'],
    sampleSentence: 'The boy plays with his dog in the park.',
    zh: '公園裡,男孩和狗在玩什麼?',
  },
  {
    id: 'pt-zoo',
    imagePrompt: `${STYLE}, a monkey eating a banana on a tree and a big elephant nearby at the zoo`,
    expectedKeywords: ['monkey', 'elephant', 'zoo', 'banana', 'eat', 'tree', 'big'],
    sampleSentence: 'The monkey eats a banana at the zoo.',
    zh: '動物園裡有哪些動物?牠們在做什麼?',
  },
  {
    id: 'pt-cat',
    imagePrompt: `${STYLE}, a cute white cat sleeping on a bed in a cozy bedroom, soft blanket`,
    expectedKeywords: ['cat', 'sleep', 'bed', 'white', 'room', 'cute'],
    sampleSentence: 'The white cat sleeps on the bed.',
    zh: '房間的床上有什麼動物?牠在做什麼?',
  },
  // ── 食物 ──
  {
    id: 'pt-breakfast',
    imagePrompt: `${STYLE}, a girl drinking a glass of milk and eating bread at a breakfast table, morning kitchen`,
    expectedKeywords: ['girl', 'drink', 'milk', 'eat', 'bread', 'breakfast', 'table'],
    sampleSentence: 'The girl drinks milk and eats bread.',
    zh: '早餐時間,女孩在吃什麼、喝什麼?',
  },
  {
    id: 'pt-fruit',
    imagePrompt: `${STYLE}, a fruit stand at a market with red apples and yellow bananas in baskets`,
    expectedKeywords: ['apple', 'banana', 'red', 'yellow', 'fruit', 'market', 'buy'],
    sampleSentence: 'The apples are red and the bananas are yellow.',
    zh: '市場的水果攤上有什麼水果?是什麼顏色?',
  },
  {
    id: 'pt-juice',
    imagePrompt: `${STYLE}, a thirsty boy drinking a cup of apple juice in a kitchen, smiling`,
    expectedKeywords: ['boy', 'drink', 'juice', 'apple', 'cup', 'kitchen', 'thirsty'],
    sampleSentence: 'The boy drinks apple juice.',
    zh: '男孩口渴了,他在廚房喝什麼?',
  },
  // ── 顏色數字 ──
  {
    id: 'pt-birthday',
    imagePrompt: `${STYLE}, a birthday party with a cake with six candles on a table and colorful balloons, kids celebrating`,
    expectedKeywords: ['birthday', 'cake', 'balloon', 'candle', 'six', 'party', 'happy'],
    sampleSentence: 'There are six candles on the birthday cake.',
    zh: '生日派對上有什麼?數數看蛋糕上有幾根蠟燭?',
  },
  {
    id: 'pt-school-things',
    imagePrompt: `${STYLE}, school supplies on a wooden desk, a blue pencil, a red pen, a ruler and an eraser next to an open pencil case`,
    expectedKeywords: ['pencil', 'pen', 'ruler', 'eraser', 'blue', 'red', 'desk'],
    sampleSentence: 'The blue pencil and the red pen are on the desk.',
    zh: '書桌上有哪些文具?是什麼顏色?',
  },
];

/** 一個場景的靜態圖片路徑(public/ 下,Vite 以根目錄供應)。 */
export function pictureTalkImageUrl(scene: PictureTalkScene): string {
  return `/img/oral/${scene.id}.png`;
}
